import { useEditor, EditorContent, type Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import {
  availableTemplateTokens,
  EMAIL_CONTENT_WIDTH,
  parseColumnWidth,
  type Block,
} from '@blogforge/shared';
import { canvasBlockStyle } from '../helpers.js';
import { valueVariableNames } from '../lib/list-variables.js';
import { useEditorStore } from '../stores/editorStore.js';
import { RichTextToolbar } from './RichTextToolbar.js';
import { BlockAdder } from './BlockAdder.js';
import { BlockWrapper } from './BlockWrapper.js';
import type { DragPath } from 'src/types.js';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { AccordionRenderer } from './AccordionRenderer.js';
import { StepsRenderer } from './steps-block/StepsRenderer.js';
import { StatRenderer } from './stat-block/StatRenderer.js';
import { TableRenderer } from './table-block/TableRenderer.js';
import { HtmlBlockRenderer } from './html-block/HtmlBlockRenderer.js';
import { SignatureRenderer } from './signature-block/SignatureRenderer.js';
import { ImageRenderer } from './image-block/ImageRenderer.js';
import Mention from '@tiptap/extension-mention';
import suggestionConfig from '../lib/tiptap/suggestionConfig.js';
import { collectMentions, type AnyNode } from './tiptap/VariableInfusedTextEditor.js';
import { OtpRenderer } from './otp-block/OtpRenderer.js';
import { AvatarRenderer } from './avatar-block/AvatarRenderer.js';
import { ListRenderer } from './list-block/ListRenderer.js';
import { SectionRenderer } from './section-block/SectionRenderer.js';
import { ButtonRenderer } from './button-block/ButtonRenderer.js';

const MENTION_RE = /\{\{([^{}]+)\}\}/g;

/**
 * Convert TipTap HTML into tag-less HTML string.
 *   <span data-type="mention" data-id="qr" ...>{{qr}}</span>  →  {{qr}}
 *   All other tags are preserved.
 */
function stripMentions(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('span[data-type="mention"]').forEach((el) => {
    const id = el.getAttribute('data-id') ?? '';
    el.replaceWith(doc.createTextNode(`{{${id}}}`));
  });
  return doc.body.innerHTML.trim();
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Convert a template string back into TipTap-compatible HTML.
 *   {{qr}}  →  <span data-type="mention" data-id="qr" data-label="qr">@qr</span>
 */
function fromTemplateString(text: string): string {
  // Replace tokens line-by-line so we can choose how to handle newlines.
  const lines = text.split('\n');
  const htmlLines = lines.map((line) =>
    line.replace(MENTION_RE, (_, id: string) => {
      const safe = escapeHtml(id);
      return `<span data-type="mention" data-id="${safe}" data-label="${safe}" data-mention-suggestion-char="@">@${safe}</span>`;
    }),
  );

  // return htmlLines.join('\n'); // preserve newlines as text
  return htmlLines.join('<br>'); // or convert to <br>
}

interface Props {
  block: Block;
  path: DragPath;
  supportsVars?: boolean;
  rawVariableDefinitions?: Record<string, unknown> | undefined;
  /**
   * Workspace audience attribute keys. Supplied by the canvas for email
   * documents only, and merged with the document's own variables below —
   * never offered in a post or component, where the token would be built
   * literally into static HTML.
   */
  audienceAttributeKeys?: readonly string[] | undefined;
}

/**
 * The blocks the TipTap editor renders. Spelled out rather than "anything with
 * a `content` string" because a block can carry content without being rich
 * text, and because a new block type should have to opt in here.
 */
type RichTextBlock = Extract<Block, { type: 'heading' | 'paragraph' | 'quote' | 'callout' }>;

/**
 * Props of the rich-text renderer, which also needs the block to be one it can
 * edit — the dispatcher narrows it in the cases below.
 */
interface RichTextProps extends Omit<Props, 'block'> {
  block: RichTextBlock;
}

// ─────────────────────────────────────────────────────────────────
// Tiptap-powered text block (paragraph, heading, quote, callout)
// ─────────────────────────────────────────────────────────────────
function TiptapBlock({
  block,
  path,
  rawVariableDefinitions,
  audienceAttributeKeys,
  supportsVars = false,
}: RichTextProps) {
  const updateBlock = useEditorStore((s) => s.updateBlock);
  const fonts = useEditorStore((s) => s.fonts);

  const [selectedBlockId, _] = useEditorStore((s) => s.selectedBlockId) || [null, []];
  const isSelected = selectedBlockId === block.id;

  const fontFamily = block.style?.fontFamily
    ? fonts.find((f) => f.id === block.style!.fontFamily)?.name
    : undefined;
  const fontFamilyStyle = fontFamily ? `${fontFamily}, sans-serif` : undefined;
  const fontSizeStyle = block.style?.fontSize ? block.style!.fontSize! : undefined;
  const fontWeightStyle = block.style?.fontWeight ? block.style!.fontWeight! : undefined;
  const textColorStyle = block.style?.color ? block.style!.color! : undefined;
  const bgColorStyle = block.style?.backgroundColor ? block.style!.backgroundColor : undefined;
  // `backgroundGradient` is a CSS `background-image` value. It paints over the
  // background colour, which stays as the fallback for clients that cannot
  // render a gradient — so it must be declared after it.
  const bgGradientStyle = block.style?.backgroundGradient
    ? block.style!.backgroundGradient
    : undefined;
  const textAlignStyle = block.style?.textAlign ? block.style!.textAlign : undefined;

  const paddingStyles: Array<[string, string | undefined]> = [
    ['padding-top', block.style?.paddingTop],
    ['padding-right', block.style?.paddingRight],
    ['padding-bottom', block.style?.paddingBottom],
    ['padding-left', block.style?.paddingLeft],
  ];

  const styles = [];

  for (const [prop, value] of paddingStyles) {
    if (value) styles.push(`${prop}: ${value};`);
  }

  if (bgColorStyle) {
    styles.push(`background-color: ${bgColorStyle};`);
  }

  if (bgGradientStyle) {
    styles.push(`background-image: ${bgGradientStyle};`);
  }

  if (fontFamilyStyle) {
    styles.push(`font-family: ${fontFamilyStyle};`);
  }

  if (fontSizeStyle) {
    styles.push(`font-size: ${fontSizeStyle};`);
  }

  if (fontWeightStyle) {
    styles.push(`font-weight: ${fontWeightStyle};`);
  }

  if (textColorStyle) {
    styles.push(`color: ${textColorStyle};`);
  }

  if (textAlignStyle) {
    styles.push(`text-align: ${textAlignStyle}`);
  }

  // The border the inspector sets, drawn here too: a block whose border shows in
  // the email but not on the canvas reads as a control that does nothing.
  if (block.style?.border) {
    styles.push(`border: ${block.style.border};`);
  }

  if (block.style?.borderColor) {
    styles.push(`border-color: ${block.style.borderColor};`);
  }

  // Offered tokens = the document's own variables + the workspace's audience
  // attributes, minus the reserved system names. One shared rule
  // (availableTemplateTokens) so this picker and the inspectors cannot drift,
  // and one shared reading of the document (valueVariableNames) so a list key —
  // which holds variables rather than a value — is never offered.
  const variableOptions = availableTemplateTokens({
    documentVariables: valueVariableNames(rawVariableDefinitions),
    workspaceAttributes: audienceAttributeKeys,
  });

  const extensions: Extensions = [
    StarterKit,
    Link.configure({ openOnClick: false }),
    Underline,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    TextStyle,
    Color,
  ];

  if (supportsVars && variableOptions && variableOptions.length > 0) {
    extensions.push(
      Mention.configure({
        HTMLAttributes: {
          class: 'bg-accent/10 text-accent px-1 py-0.5 rounded cursor-pointer hover:bg-accent/20',
        },
        suggestion: suggestionConfig(variableOptions),
      }),
    );
  }

  const editor = useEditor({
    extensions,
    content: supportsVars ? fromTemplateString(block.content) : block.content,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      updateBlock(block.id, { content: supportsVars ? stripMentions(html) : html }, path);
      if (supportsVars) {
        const json = editor.getJSON() as AnyNode;
        const mentions = collectMentions(json);
        updateBlock(block.id, { contentHasVar: mentions.length > 0 }, path);
      }
    },
    editorProps: {
      attributes: {
        style: styles.join(' '),
      },
    },
  });

  return (
    <div className="relative">
      {isSelected && editor && <RichTextToolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main block renderer — dispatches to the right component
// ─────────────────────────────────────────────────────────────────
export function BlockRenderer({
  block,
  path,
  rawVariableDefinitions,
  audienceAttributeKeys,
}: Omit<Props, 'supportsVars'>) {
  switch (block.type) {
    case 'heading':
      return (
        <TiptapBlock
          block={block}
          path={path}
          supportsVars
          rawVariableDefinitions={rawVariableDefinitions}
          audienceAttributeKeys={audienceAttributeKeys}
        />
      );

    case 'paragraph':
      return (
        <TiptapBlock
          block={block}
          path={path}
          supportsVars
          rawVariableDefinitions={rawVariableDefinitions}
          audienceAttributeKeys={audienceAttributeKeys}
        />
      );

    case 'quote':
      return (
        <blockquote className="doc-quote">
          <TiptapBlock
            block={block}
            path={path}
            supportsVars
            rawVariableDefinitions={rawVariableDefinitions}
            audienceAttributeKeys={audienceAttributeKeys}
          />
          {block.attrs?.citation && (
            <cite className="text-muted mt-1 block text-xs">— {block.attrs.citation}</cite>
          )}
        </blockquote>
      );

    case 'callout': {
      const variantStyles: Record<string, string> = {
        info: 'bg-accent/8 border-accent-border',
        warning: 'bg-warn/10 border-warn/60',
        success: 'bg-ok/10 border-ok',
        danger: 'bg-danger/8 border-danger/40',
      };
      return (
        <div
          className={`my-2 rounded-r-lg border-l-4 px-4 py-3 ${variantStyles[block.attrs.variant] ?? variantStyles['info']}`}
          // An author-set border frames the callout in the email, so it frames it
          // here too — the variant's left bar is a separate accent.
          style={{
            ...(block.style?.border ? { border: block.style.border } : {}),
            ...(block.style?.borderColor ? { borderColor: block.style.borderColor } : {}),
          }}
        >
          <TiptapBlock
            block={block}
            path={path}
            supportsVars
            rawVariableDefinitions={rawVariableDefinitions}
            audienceAttributeKeys={audienceAttributeKeys}
          />
        </div>
      );
    }

    case 'image':
      return <ImageRenderer block={block} path={path} rawDefinitions={rawVariableDefinitions} />;

    case 'avatar':
      return <AvatarRenderer block={block} path={path} rawDefinitions={rawVariableDefinitions} />;

    case 'divider':
      return <hr className="doc-border" />;

    case 'button': {
      return <ButtonRenderer block={block} path={path} />;
    }

    case 'embed':
      return (
        <div className="border-line bg-bg-soft text-muted my-4 rounded-lg border border-dashed p-4 text-center text-sm">
          <p className="text-ink-soft font-medium">{block.attrs.provider} embed</p>
          <p className="text-muted mt-1 truncate text-xs">{block.attrs.url}</p>
        </div>
      );

    case 'steps':
      return <StepsRenderer block={block} />;

    case 'html':
      return <HtmlBlockRenderer block={block} />;

    case 'columns': {
      const columnBlocks = (block.attrs.columnBlocks as Block[][] | undefined) ?? [];
      const colCount = Math.max(block.attrs.columns ?? columnBlocks.length, columnBlocks.length, 1);
      // Mirror the email renderer: a pinned column keeps its length and the
      // columns that fill share what is left (see parseColumnWidth).
      const columnWidths = (block.attrs.columnWidths as (string | null)[] | undefined) ?? [];
      const gridTemplateColumns = Array.from({ length: colCount }, (_, i) =>
        parseColumnWidth(columnWidths[i], EMAIL_CONTENT_WIDTH),
      )
        .map((width) => width ?? 'minmax(0,1fr)')
        .join(' ');
      return (
        <div className="border-line my-4 rounded-lg border border-dashed p-3">
          <p className="text-muted mb-2 text-[11px] font-semibold tracking-[0.12em] uppercase">
            Columns
          </p>
          {/* The row's own style goes here rather than on the dashed chrome
              around it: that chrome is the editor's frame, and this grid is the
              thing the email's table actually is — border, background, padding
              and margins all land on it, so the preview shows the box the
              message will have without hiding the frame that says "row". */}
          <div
            className="grid gap-6"
            style={{ gridTemplateColumns, ...canvasBlockStyle(block.style) }}
          >
            {Array.from({ length: colCount }).map((_, colIndex) => {
              const colBlocks = columnBlocks[colIndex] ?? [];
              const colPath = [
                ...path,
                { id: block.id, type: 'columns' as const, column: colIndex },
              ];
              return (
                <div
                  key={colIndex}
                  className="border-line/70 bg-bg-soft/30 flex min-h-16 flex-col rounded-lg border border-dashed p-2"
                >
                  {colBlocks.length === 0 ? (
                    <p className="text-muted py-2 text-center text-xs">
                      Empty column — add a block below
                    </p>
                  ) : (
                    <SortableContext
                      items={colBlocks.map((c) => c.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {colBlocks.map((b) => (
                          <BlockWrapper
                            key={b.id}
                            block={b}
                            path={colPath}
                            rawVariableDefinitions={rawVariableDefinitions}
                            audienceAttributeKeys={audienceAttributeKeys}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  )}
                  <BlockAdder
                    parent={{ id: block.id, type: 'columns', column: colIndex }}
                    path={path}
                  />
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    case 'accordion': {
      return <AccordionRenderer block={block} path={path} isEditing />;
    }

    // case 'section': {
    //   const parent = path.length > 0 ? path[path.length - 1] : null;

    //   const {
    //     backgroundType,
    //     backgroundSrc,
    //     minHeight,
    //     overlayColor,
    //     blocks: nestedBlocks,
    //   } = block.attrs;

    //   const inner = (nestedBlocks as Block[]).map((b) => (
    //     <BlockWrapper
    //       block={b}
    //       key={b.id}
    //       path={[...path, { id: block.id, type: block.type }]}
    //       rawVariableDefinitions={rawVariableDefinitions}
    //       audienceAttributeKeys={audienceAttributeKeys}
    //     />
    //   ));

    //   return (
    //     <div
    //       className="border-line relative flex min-h-16 flex-col items-center justify-center rounded-lg border border-dashed p-3"
    //       style={{ minHeight: minHeight ?? undefined }}
    //     >
    //       {overlayColor && (
    //         <div
    //           className="absolute inset-0 z-[1] rounded-lg opacity-20"
    //           style={{ backgroundColor: overlayColor }}
    //         />
    //       )}

    //       <div className="z-[2] w-full space-y-4">
    //         <div
    //           className={cn('z-[2] flex w-full gap-4', {
    //             'flex-col': block.style?.flexDirection === 'column' || !block.style?.flexDirection,
    //             'flex-row': block.style?.flexDirection === 'row',
    //           })}
    //         >
    //           <SortableContext
    //             items={(nestedBlocks as Block[]).map((c) => c.id)}
    //             strategy={verticalListSortingStrategy}
    //           >
    //             {inner}
    //           </SortableContext>
    //         </div>

    //         <BlockAdder
    //           parent={{
    //             id: block.id,
    //             type: 'section',
    //             isSectionInAccordion: parent?.type === 'accordion',
    //           }}
    //           path={path}
    //         />
    //       </div>
    //     </div>
    //   );
    // }
    case 'section': {
      return (
        <SectionRenderer
          block={block}
          path={path}
          rawVariableDefinitions={rawVariableDefinitions}
          audienceAttributeKeys={audienceAttributeKeys}
        />
      );
    }

    case 'stat':
      return <StatRenderer block={block} />;

    case 'table':
      return <TableRenderer block={block} />;

    case 'signature': {
      return <SignatureRenderer block={block} />;
    }

    case 'otp': {
      return <OtpRenderer block={block} path={path} rawDefinitions={rawVariableDefinitions} />;
    }

    case 'list': {
      return (
        <ListRenderer
          block={block}
          path={path}
          rawVariableDefinitions={rawVariableDefinitions}
          audienceAttributeKeys={audienceAttributeKeys}
        />
      );
    }

    default: {
      const _: never = block;
      return <div className="text-danger p-2 text-xs">Unknown block type: {(_ as Block).type}</div>;
    }
  }
}
